import { useCallback } from 'react';

const FACEBOOK_SDK_ID = 'facebook-jssdk';
const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

// The part of the Facebook JS SDK this hook uses
interface FacebookLoginResponse {
  authResponse?: { accessToken?: string } | null;
}

interface FacebookSdk {
  init(params: {
    appId: string;
    cookie: boolean;
    xfbml: boolean;
    version: string;
  }): void;
  login(
    callback: (response: FacebookLoginResponse) => void,
    options?: { scope: string },
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
  }
}

let sdkLoadPromise: Promise<FacebookSdk> | undefined;
let initializedAppId: string | undefined;

const loadFacebookSdk = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('Facebook SDK is unavailable on server side'),
    );
  }

  if (window.FB) {
    return Promise.resolve(window.FB);
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise<FacebookSdk>((resolve, reject) => {
    const existingScript = document.getElementById(FACEBOOK_SDK_ID);

    const handleReady = () => {
      if (window.FB) {
        resolve(window.FB);
      } else {
        reject(new Error('Facebook SDK loaded without FB global'));
      }
    };

    if (existingScript) {
      existingScript.addEventListener('load', handleReady, { once: true });
      existingScript.addEventListener(
        'error',
        () => {
          reject(new Error('Failed to load Facebook SDK'));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = FACEBOOK_SDK_ID;
    script.async = true;
    script.defer = true;
    script.src = FACEBOOK_SDK_SRC;
    script.onload = handleReady;
    script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
    document.body.appendChild(script);
  });

  return sdkLoadPromise;
};

const initFacebookSdk = async (appId?: string) => {
  const FB = await loadFacebookSdk();
  if (!appId) {
    throw new Error('Missing Facebook app id');
  }

  if (initializedAppId !== appId) {
    FB.init({
      appId,
      cookie: true,
      xfbml: false,
      version: 'v20.0',
    });
    initializedAppId = appId;
  }

  return FB;
};

export const useFacebookLogin = (appId?: string) => {
  return useCallback(async () => {
    const FB = await initFacebookSdk(appId);

    return new Promise<string>((resolve, reject) => {
      FB.login(
        (response) => {
          const accessToken = response?.authResponse?.accessToken;
          if (accessToken) {
            resolve(accessToken);
            return;
          }
          reject(new Error('Facebook auth cancelled or failed'));
        },
        { scope: 'public_profile,email' },
      );
    });
  }, [appId]);
};
